// Push notifications: new group messages and evening weather tips. Turned on under More → Notifications.
// Two kinds, same switches and server API:
//  - web push in browsers and home-screen apps: group pushes carry no text, the service worker (sw/sw.ts) fetches and
//    decrypts the message and shows who wrote what;
//  - Firebase (FCM) in the native Android app, whose web view has no web push: group pushes say only that there's a
//    new message (the server can't read it), and tapping opens the group.
// For tips the server keeps your rough area (about 10 km) and your fishing/hunting choices, with no name.
import { b64uDecode, b64uEncode } from "../shared/b64u.ts";
import type { Group } from "../shared/model.ts";
import { VAPID_PUBLIC_KEY } from "./config.ts";
import { PushNotifications, hasNativePush, isNative } from "./native.ts";
import { dbPut } from "./db.ts";
import { $maybe, el, toast } from "./dom.ts";
import { LANG, t } from "./i18n.ts";
import { onRender } from "./state.ts";
import { lsGet, lsSet } from "./util.ts";
import { setView } from "./views.ts";
import { groups, me, syncAll } from "./groups/store.ts";
import { openChat } from "./groups/chat.ts";
import { learnedPatterns } from "./insights.ts";
import { hasPlus } from "./billing.ts";
import { hasProfile, openProfile, profile, tipsPlace } from "./tips.ts";

const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
const standalone = () => matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;
const webPushSupported = () => !isNative() && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window && location.protocol === "https:" && !!VAPID_PUBLIC_KEY;
const pushSupported = () => hasNativePush() || webPushSupported();
const pushPrefs = () => ({ msgs: lsGet("pushMsgs", "0") === "1", tips: lsGet("pushTips", "0") === "1" });

/** Where pushes for this phone go: a web push subscription, or the native app's Firebase token. */
type PushTarget = PushSubscriptionJSON | { fcm: string };
let pushSub: PushTarget | null = null;
/** What the server calls this target ("endpoint" when turning things off). */
const address = (s: PushTarget) => ("fcm" in s ? "fcm:" + s.fcm : s.endpoint ?? "");
/** This phone's subscription id (same as the server's targetId()), so the relay can skip the sender. */
export let subId = lsGet("subId", "");
/** Notification permission: "granted", "denied" or "default"/"prompt" (the native one is read at start). */
let nativePerm = "prompt";
const permission = () => (isNative() ? nativePerm : "Notification" in window ? Notification.permission : "default");

async function setTarget(s: PushTarget | null): Promise<void> {
  pushSub = s;
  subId = s ? b64uEncode(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(address(s)))).slice(0, 22) : "";
  lsSet("subId", subId);
}

/** The Firebase token from the native app (asks for permission when `create`). */
async function nativeToken(create: boolean): Promise<string | null> {
  let perm = (await PushNotifications.checkPermissions()).receive;
  if (perm !== "granted" && create) perm = (await PushNotifications.requestPermissions()).receive;
  nativePerm = perm;
  if (perm !== "granted") return null;
  return new Promise<string>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("no FCM token")), 20000);
    void PushNotifications.addListener("registration", (tk) => { clearTimeout(timer); resolve(tk.value); });
    void PushNotifications.addListener("registrationError", (e) => { clearTimeout(timer); reject(new Error(e.error)); });
    void PushNotifications.register();
  });
}

/** Finds (or with `create`, makes) this phone's push target. */
async function getSub(create: boolean): Promise<PushTarget | null> {
  if (isNative()) {
    if (!hasNativePush()) return null;
    const token = await nativeToken(create);
    await setTarget(token ? { fcm: token } : null);
    return pushSub;
  }
  const reg = await navigator.serviceWorker.ready;
  let s = await reg.pushManager.getSubscription();
  if (!s && create) s = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64uDecode(VAPID_PUBLIC_KEY) });
  if (s) await setTarget(s.toJSON());
  return pushSub;
}
async function dropSub(): Promise<void> {
  if (isNative()) await PushNotifications.unregister().catch(() => {});
  else await (await (await navigator.serviceWorker.ready).pushManager.getSubscription())?.unsubscribe();
  await setTarget(null);
}
async function askPermission(): Promise<boolean> {
  if (isNative()) return (nativePerm = (await PushNotifications.requestPermissions()).receive) === "granted";
  return (await Notification.requestPermission()) === "granted";
}

async function pushApi(body: object, headers: Record<string, string> = {}): Promise<void> {
  const r = await fetch("/api/push", { method: "POST", headers: { "content-type": "application/json", ...headers }, body: JSON.stringify(body), signal: AbortSignal.timeout(20000) });
  if (!r.ok) throw new Error("push api " + r.status);
}
export async function groupPushOn(g: Group): Promise<void> {
  if (pushPrefs().msgs && pushSub) await pushApi({ action: "group-on", g: g.id, sub: pushSub, lang: LANG }, { "x-group-token": g.token });
}
export async function groupPushOff(g: Group): Promise<void> {
  if (pushSub) await pushApi({ action: "group-off", g: g.id, endpoint: address(pushSub) }, { "x-group-token": g.token }).catch(() => {});
}
async function sendTipsSub(): Promise<void> {
  const place = tipsPlace();
  if (!place || !pushSub) throw new Error("no place");
  // rounded to about 10 km here already, so your exact spot never leaves the phone
  await pushApi({ action: "tips-on", sub: pushSub, lat: Math.round(place.lat * 10) / 10, lon: Math.round(place.lon * 10) / 10, lang: LANG,
    profile: { ...profile, home: undefined, learned: hasPlus() ? learnedPatterns() : [] } }); // the matching tip is Plus
  lsSet("tipsSent", String(Date.now()));
}
/** Called when the profile, language or learned patterns change, so the evening tips stay right. */
export function pushProfileChanged(): void {
  if (pushPrefs().tips && pushSub) sendTipsSub().catch((e) => console.warn("Tip settings not sent", e));
}

const unsupportedNote = () => t(isNative() ? "pushNativeSoon" : isIOS && !standalone() ? "pushNeedHome" : "pushUnsupported");

async function setPush(kind: "msgs" | "tips", on: boolean): Promise<void> {
  const key = kind === "msgs" ? "pushMsgs" : "pushTips";
  try {
    if (on) {
      if (!pushSupported()) { toast(unsupportedNote()); return; }
      if (kind === "tips" && !hasProfile()) { toast(t("pushNeedProfile")); openProfile(); return; }
      if (kind === "tips" && !tipsPlace()) { toast(t("tipsNoPlace")); openProfile(); return; }
      if (!(await askPermission())) { toast(t("pushDenied")); return; }
      if (!(await getSub(true))) throw new Error("no push target");
      lsSet(key, "1");
      if (kind === "msgs") for (const g of groups) await groupPushOn(g);
      else await sendTipsSub();
      toast(t(kind === "msgs" ? "pushMsgsOn" : "pushTipsOn"));
    } else {
      if (kind === "msgs") for (const g of groups) await groupPushOff(g);
      else if (pushSub) await pushApi({ action: "tips-off", endpoint: address(pushSub) }).catch(() => {});
      lsSet(key, "0");
      // neither kind on: drop the subscription altogether
      if (!pushPrefs().msgs && !pushPrefs().tips) await dropSub().catch(() => {});
    }
  } catch (e) { console.error(e); lsSet(key, on ? "0" : lsGet(key, "0")); toast(t("pushFailed")); }
  finally { renderPushPanel(); }
}

function renderPushPanel(): void {
  const box = $maybe("#push-panel");
  if (!box) return;
  const prefs = pushPrefs(), perm = permission();
  let note = "";
  if (!pushSupported()) note = unsupportedNote();
  else if (perm === "denied") note = t("pushBlocked");
  const row = (kind: "msgs" | "tips", on: boolean, title: string, text: string) => el("label", { class: "pushrow" },
    el("span", { class: "pushtext" }, el("b", { text: title }), el("small", { text })),
    el("input", { type: "checkbox", class: "switch", checked: on, disabled: !pushSupported() || perm === "denied",
      onchange: (e) => void setPush(kind, e.currentTarget.checked) }));
  box.replaceChildren(
    row("msgs", prefs.msgs, t("pushMsgs"), t(hasNativePush() ? "pushMsgsTextNative" : "pushMsgsText")),
    row("tips", prefs.tips, t("pushTips"), t("pushTipsText")),
    note ? el("p", { class: "hint warn", text: note }) : "");
}

// The service worker needs the language and your id to word notifications (it can't read the app's settings).
function saveSwSettings(): void {
  dbPut("kv", { id: "settings", lang: LANG, meId: me.id }).catch(() => {});
}

/** Opening the app from a notification. */
function openFromNotification(d: { open?: string; g?: string }): void {
  if (d.open === "chat" && d.g && groups.some((g) => g.id === d.g)) { setView("groups"); void openChat(d.g); }
  else if (d.open === "tips") setView("log");
}
if ("serviceWorker" in navigator) navigator.serviceWorker.addEventListener("message", (e) => { if (e.data) openFromNotification(e.data); });

export async function pushStart(): Promise<void> {
  saveSwSettings();
  if (hasNativePush()) {
    // native: a tap on a notification opens the group or the tips; while the app is open, a new message just syncs
    void PushNotifications.addListener("pushNotificationActionPerformed", (a) => openFromNotification(a.notification.data as { open?: string; g?: string }));
    void PushNotifications.addListener("pushNotificationReceived", () => void syncAll());
    void PushNotifications.createChannel({ id: "reelhunt", name: "ReelHunt", importance: 4, visibility: 1 }).catch(() => {});
    nativePerm = (await PushNotifications.checkPermissions().catch(() => ({ receive: "prompt" }))).receive;
  }
  renderPushPanel();
  const q = new URLSearchParams(location.search);
  const chat = q.get("chat");
  if (chat) openFromNotification({ open: "chat", g: chat });
  if (chat || q.get("view")) history.replaceState(null, "", location.pathname);
  if (!pushSupported() || (!pushPrefs().msgs && !pushPrefs().tips)) return;
  try {
    const before = subId;
    await getSub(false);
    // the phone's address can change (Firebase renews tokens now and then): tell the server the new one
    const changed = !!pushSub && subId !== before;
    if (changed && pushPrefs().msgs) for (const g of groups) await groupPushOn(g);
    // keep the server's copy of the tip settings fresh (language, learned patterns) about once a day
    if (pushPrefs().tips && pushSub && (changed || Date.now() - Number(lsGet("tipsSent", "0")) > 864e5)) await sendTipsSub();
  } catch (e) { console.warn("Push start", e); }
}
onRender(() => { renderPushPanel(); saveSwSettings(); });
